import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";

const products = [
  {
    id: "dell-rugged-10",
    name: "Dell Pro Rugged 10 Tablet",
    badge: "Compact",
    brand: "Dell Rugged",
    tagline: "World's Lightest 10\" Fully-Rugged Windows Tablet — 1.04kg, IP66",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/d051876b1c1591957c4ce5f5ac1ec8bd.png",
    specs: [
      { label: "Display", value: "10\" 1000-nit sunlight-readable, glove-touch" },
      { label: "OS", value: "Windows 11 Pro (Copilot+ PC)" },
      { label: "Processor", value: "Intel Core Ultra 200V series" },
      { label: "Durability", value: "MIL-STD-810H, IP66, FirstNet Ready" },
      { label: "Weight", value: "1.04kg (2.29 lbs) — world's lightest 10\" rugged" },
      { label: "Connectivity", value: "Wi-Fi 7, optional 5G, BT" },
      { label: "Battery", value: "Hot-swappable, all-day" },
      { label: "AI", value: "40+ TOPS NPU, Intel Arc graphics" },
    ],
    features: [
      "World's lightest 10\" fully-rugged Windows tablet at just 1.04kg",
      "IP66 rated — superior dust and water jet protection",
      "1000-nit direct sunlight-viewable, glove-touch capable display",
      "Intel Core Ultra 200V series with 40+ TOPS NPU — Copilot+ AI PC",
      "Wi-Fi 7 and optional 5G for seamless field connectivity",
      "Hot-swappable batteries for uninterrupted all-day operation",
      "FirstNet Ready with Band 14 for first responder networks",
      "Dell ProManage for centralized fleet management",
    ],
  },
  {
    id: "dell-rugged-12",
    name: "Dell Pro Rugged 12 Tablet",
    badge: "Popular",
    brand: "Dell Rugged",
    tagline: "12\" Fully-Rugged Tablet — 1200-nit Display, Dual Hot-Swap Battery",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/b44b30507261be801eeddd2018b274b6.png",
    specs: [
      { label: "Display", value: "12\" 1200-nit sunlight-readable, glove-touch" },
      { label: "OS", value: "Windows 11 Pro (Copilot+ PC)" },
      { label: "Processor", value: "Intel Core Ultra 200V series" },
      { label: "Durability", value: "MIL-STD-810H, IP66, FirstNet Ready" },
      { label: "Battery", value: "Dual hot-swappable batteries" },
      { label: "Connectivity", value: "Wi-Fi 7, optional 5G, BT" },
      { label: "AI", value: "40+ TOPS NPU, Intel Arc graphics" },
      { label: "Options", value: "Keyboard dock, barcode scanner" },
    ],
    features: [
      "1200-nit direct sunlight-viewable display — readable in any outdoor condition",
      "Dual hot-swappable batteries for all-day uninterrupted productivity",
      "Intel Core Ultra 200V series with Copilot+ AI PC capabilities",
      "Wi-Fi 7 with Dual Band Support and optional 5G connectivity",
      "MIL-STD-810H certified for drops, vibration, and extreme temperatures",
      "Optional keyboard dock converts tablet into a full laptop",
      "FirstNet Ready with Band 14 for first responder networks",
      "Dell ProManage for centralized remote device management",
    ],
  },
  {
    id: "dell-rugged-13",
    name: "Dell Pro Rugged 13",
    badge: "Flagship",
    brand: "Dell Rugged",
    tagline: "World's Smallest 13\" Fully-Rugged AI PC — 1400-nit, IP65, 6ft Drop",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/77c5f3c398a8927cfa97baac1ae31be7.png",
    specs: [
      { label: "Display", value: "13\" 1400-nit, low blue light, resistive touchpad" },
      { label: "Processor", value: "Intel Core Ultra U and H-series" },
      { label: "Durability", value: "MIL-STD-810H, IP65, 6ft (1.8m) drop-tested" },
      { label: "Operating Temp", value: "-20°F to 145°F (-29°C to 63°C)" },
      { label: "Battery", value: "Hot-swappable, extended runtime" },
      { label: "Connectivity", value: "Wi-Fi 6E, optional 5G, BT" },
      { label: "Form Factor", value: "World's smallest 13\" fully-rugged AI PC" },
      { label: "Ports", value: "USB-A, USB-C, HDMI, RJ45, SD" },
    ],
    features: [
      "World's smallest 13\" fully-rugged AI PC — designed for peak performance",
      "1400-nit bright screen with low blue light — perfect outdoor visibility",
      "IP65 certified — fully protected against dust and water jets",
      "Drop-tested from 6 feet — survives real-world field accidents",
      "Operates from -20°F to 145°F — extreme temperature range",
      "Resistive touchpad works with gloves in any weather condition",
      "Hot-swappable batteries for zero-downtime field operations",
      "Optional 5G and Wi-Fi 6E for connectivity in remote locations",
    ],
  },
  {
    id: "dell-rugged-14",
    name: "Dell Pro Rugged 14",
    badge: "Flagship",
    brand: "Dell Rugged",
    tagline: "World's Smallest & Lightest 14\" Semi-Rugged AI PC — Optional NVIDIA RTX 500",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/de10131c1dd492bfb5ee3785ed3b1945.png",
    specs: [
      { label: "Display", value: "14\" 1100-nit, low blue light, glove multi-touch" },
      { label: "Processor", value: "Intel Core Ultra (optional NVIDIA RTX 500 Ada)" },
      { label: "Durability", value: "MIL-STD-810H, IP53, 3ft (0.9m) drop-tested" },
      { label: "Operating Temp", value: "-20°F to 145°F (-29°C to 63°C)" },
      { label: "Battery", value: "Hot-swappable batteries" },
      { label: "Connectivity", value: "Wi-Fi 7, optional 5G, BT" },
      { label: "Form Factor", value: "World's smallest & lightest 14\" semi-rugged AI PC" },
      { label: "GPU Option", value: "NVIDIA RTX 500 Ada Generation" },
    ],
    features: [
      "World's smallest and lightest 14\" semi-rugged AI PC",
      "Optional NVIDIA RTX 500 Ada Gen GPU for graphics-intensive field apps",
      "1100-nit display with low blue light — clear visibility in direct sunlight",
      "Glove multi-touch display for easy field interaction",
      "IP53 rated — protected against dust and water spray",
      "Drop-tested from 3 feet — built for semi-rugged field environments",
      "Hot-swappable batteries and optional 5G and Wi-Fi 7",
      "Operates from -20°F to 145°F for extreme environment deployment",
    ],
  },
  {
    id: "eda10a",
    name: "ScanPal EDA10A Tablet",
    badge: "Popular",
    brand: "Honeywell",
    tagline: "Lightweight 10\" Enterprise Android Tablet — 5G, Wi-Fi 6, 8000mAh",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/0d8551992a549df2d1d39c5a9bcdb230.jpeg",
    specs: [
      { label: "OS", value: "Android 12, upgradeable to Android 14" },
      { label: "Display", value: "10.1\" FHD 1920×1200, 550-nit, Gorilla Glass 5" },
      { label: "Processor", value: "Qualcomm Snapdragon SM4350-AC, Octa-Core 2.2GHz" },
      { label: "Memory", value: "4GB/64GB or 8GB/128GB; microSD up to 2TB" },
      { label: "Connectivity", value: "5G, Wi-Fi 6 (802.11ax), BT 5.1, NFC" },
      { label: "Durability", value: "IP65, MIL-STD-810H, 1.3m drop (2.0m with boot)" },
      { label: "Battery", value: "8,000mAh Li-Ion, hot-swappable, 12+ hrs" },
      { label: "Scanner", value: "Honeywell S0703 Slim Imager (1D/2D)" },
    ],
    features: [
      "674g lightweight design — easy to carry and use all day",
      "Gorilla Glass 5 touchscreen — supports glove, wet hand, and oily hand touch",
      "8,000mAh hot-swappable battery — 12+ hours of continuous operation",
      "5G and Wi-Fi 6 for high-speed connectivity in dense environments",
      "Honeywell S0703 Slim Imager — fast 1D/2D barcode scanning",
      "16MP rear + 8MP front cameras for documentation and video calls",
      "MIL-STD-810H certified — 1.3m drop (2.0m with optional protective boot)",
      "Android 12 upgradeable to 14 via Honeywell Mobility Edge platform",
    ],
  },
  {
    id: "rt10-android",
    name: "RT10 Rugged Tablet — Android",
    badge: "Versatile",
    brand: "Honeywell",
    tagline: "10\" Android Rugged Tablet — FlexRange Scanner, Hot-Swap, Vehicle Dock",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/ac0c017b0d380fccfdf16d650695a809.jpeg",
    specs: [
      { label: "OS", value: "Android 9, upgradeable to Android 13 (committed to 14)" },
      { label: "Display", value: "10.1\" 800-nit, 1920×1200, optically bonded" },
      { label: "Processor", value: "Qualcomm Snapdragon 64-bit Octa-Core 2.2GHz" },
      { label: "Memory", value: "4GB/6GB/8GB RAM; 32GB/64GB/128GB storage" },
      { label: "Connectivity", value: "4G LTE (CAT13, up to 400Mbps), Wi-Fi 5, BT 5.0, NFC" },
      { label: "Durability", value: "IP65, MIL-STD-810G, 1.2m (4ft) drop" },
      { label: "Battery", value: "5,900mAh (12hr) or 10,280mAh (18hr), hot-swap" },
      { label: "Scanner", value: "FlexRange 1D/2D Imager — 0.1m to 10.7m range" },
    ],
    features: [
      "FlexRange dual-lens imager scans from 5 inches to 35 feet — no repositioning needed",
      "Hot-swappable battery — 12hr standard or 18hr extended, zero downtime",
      "800-nit optically bonded display — readable indoors and outdoors",
      "4G LTE CAT13 up to 400Mbps downlink for fast data sync",
      "NFC integrated for tap-to-pair and asset tracking",
      "Vehicle dock compatible — ideal for forklift and fleet deployment",
      "Honeywell Mobility Edge — Android OS support through version 13 (committed to 14)",
      "Smart Talk PTT, text, voice and video communications built-in",
    ],
  },
  {
    id: "rt10-windows",
    name: "RT10 Rugged Tablet — Windows",
    badge: "Versatile",
    brand: "Honeywell",
    tagline: "10\" Windows Rugged Tablet — Full Desktop Apps, FlexRange Scanner",
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/4b6a9df7e8aaf38045343137817e7efb.jpeg",
    specs: [
      { label: "OS", value: "Windows 10 Enterprise LTSC" },
      { label: "Display", value: "10.1\" 800-nit, 1920×1200, optically bonded" },
      { label: "Processor", value: "Intel Pentium N4200 Apollo Lake, up to 2.5GHz" },
      { label: "Memory", value: "8GB RAM, 128GB storage" },
      { label: "Connectivity", value: "4G LTE (CAT6, up to 300Mbps), Wi-Fi 5, BT 5.0, GPS" },
      { label: "Durability", value: "IP65, MIL-STD-810G, 1.2m (4ft) drop" },
      { label: "Battery", value: "5,900mAh (12hr) or 10,280mAh (18hr), hot-swap" },
      { label: "Scanner", value: "FlexRange 1D/2D Imager — 0.1m to 10.7m range" },
    ],
    features: [
      "Windows 10 Enterprise LTSC — runs full Windows desktop and legacy applications",
      "FlexRange dual-lens imager scans from 5 inches to 35 feet",
      "Hot-swappable battery — 12hr standard or 18hr extended, zero downtime",
      "800-nit optically bonded display — readable indoors and outdoors",
      "TPM v2.0 and Kensington Lock slot for enterprise security",
      "4G LTE CAT6 up to 300Mbps for reliable field connectivity",
      "Vehicle dock compatible — ideal for forklift and fleet deployment",
      "Micro HDMI, USB 3.0 Type-A & Type-C, 3.5mm audio, microSD ports",
    ],
  },
];

const whyRugged = [
  { icon: "ri-shield-check-line", title: "MIL-STD-810H Certified", desc: "Both Honeywell and Dell Rugged devices carry MIL-STD-810H certification — tested against drops, vibration, humidity, altitude, and extreme temperatures." },
  { icon: "ri-time-line", title: "3x Longer Device Lifespan", desc: "Rugged devices last 3–5 years longer than consumer hardware in field environments, dramatically reducing total cost of ownership." },
  { icon: "ri-battery-charge-line", title: "Hot-Swap Batteries", desc: "Never lose a shift to a dead battery. Hot-swappable batteries on Honeywell and Dell Rugged devices keep teams productive 24/7." },
  { icon: "ri-settings-3-line", title: "Unified Device Management", desc: "Honeywell Mobility Edge and Dell ProManage enable centralized remote management, updates, and security policy enforcement across your entire fleet." },
  { icon: "ri-sun-line", title: "Sunlight-Readable Displays", desc: "High-brightness displays (800–1400 nit) remain fully readable in direct sunlight — critical for outdoor field operations, construction sites, and logistics yards." },
  { icon: "ri-customer-service-2-line", title: "Local Authorized Support", desc: "SE Distribution provides full local warranty, deployment support, and ongoing maintenance from our Kuala Lumpur office." },
];

const dellAccessories = [
  {
    icon: "ri-car-line",
    name: "Rugged Tablet Vehicle Docks",
    desc: "Mount your Dell Pro Rugged tablet in any vehicle with docks from Havis, PMT, and Gamber Johnson — for on-the-go productivity in trucks, vans, and fleet vehicles.",
    tags: ["Havis", "PMT", "Gamber Johnson"],
  },
  {
    icon: "ri-keyboard-line",
    name: "Dell Pro Rugged Keyboard",
    desc: "IP66-rated full-size keyboard that locks into place and converts your Dell rugged tablet into a full laptop — ideal for data-entry-heavy field workflows.",
    tags: ["IP66 Rated", "Tablet to Laptop", "Locking Design"],
  },
  {
    icon: "ri-pushpin-line",
    name: "Magnetic Tablet Mount",
    desc: "Powerful VESA magnetic backplate mounts your Dell rugged tablet to any metal surface — perfect for vehicles, machinery, and workstations.",
    tags: ["VESA Compatible", "Magnetic", "Metal Surface"],
  },
  {
    icon: "ri-briefcase-line",
    name: "Field Carrying Accessories",
    desc: "Rigid handles, shoulder straps, and rotating hand straps keep your Dell rugged tablet secure and accessible during active field operations.",
    tags: ["Rigid Handle", "Shoulder Strap", "Hand Strap"],
  },
  {
    icon: "ri-battery-charge-line",
    name: "Dual Bay Battery Charger",
    desc: "Keep spare batteries fully charged with Dell's mobile dual-bay battery charger or Lind's modular charging solutions — ensuring zero downtime between shifts.",
    tags: ["Dual Bay", "Mobile Charging", "Lind Compatible"],
  },
  {
    icon: "ri-layout-bottom-line",
    name: "Rugged Tablet Mobile Stand",
    desc: "Easily foldable stand that won't weigh you down — ideal for desk use, briefings, and presentations in the field.",
    tags: ["Foldable", "Lightweight", "Desk Use"],
  },
];

const honeywellAccessories = [
  {
    icon: "ri-car-line",
    name: "Vehicle Dock (RT10A / RT10W)",
    desc: "Forklift and fleet vehicle mounting docks for the RT10 series — keeps the tablet powered, connected, and secure while driving. Supports hot-swap charging.",
    tags: ["Forklift Mount", "Fleet Vehicle", "Hot-Swap Charging"],
  },
  {
    icon: "ri-computer-line",
    name: "Desk Dock",
    desc: "Office desk dock for the RT10 and EDA10A — charges the device and provides a stable workstation setup for supervisors and managers.",
    tags: ["RT10 Compatible", "EDA10A Compatible", "Office Use"],
  },
  {
    icon: "ri-hand-coin-line",
    name: "Rotating Hand Strap",
    desc: "Ergonomic rotating hand strap for the EDA10A and RT10 — keeps the tablet secure during active warehouse and field operations.",
    tags: ["EDA10A", "RT10", "Ergonomic"],
  },
  {
    icon: "ri-battery-2-charge-line",
    name: "Quad Bay Battery Charger",
    desc: "Charges up to 4 hot-swap batteries simultaneously — ideal for large teams running 24/7 shift operations with the RT10 or EDA10A.",
    tags: ["4-Bay", "RT10", "EDA10A"],
  },
  {
    icon: "ri-shield-line",
    name: "Protective Boot (EDA10A)",
    desc: "Optional rubber protective boot for the EDA10A increases drop specification from 1.3m to 2.0m (6.6ft) on concrete — for the most demanding environments.",
    tags: ["EDA10A", "2.0m Drop", "Rubber Boot"],
  },
  {
    icon: "ri-layout-masonry-line",
    name: "Display Dock (EDA10A)",
    desc: "Connects the EDA10A to an external monitor, keyboard, and mouse — enabling a full desktop workstation experience from a single rugged tablet.",
    tags: ["EDA10A", "External Monitor", "Workstation Mode"],
  },
];

const useCases = [
  {
    icon: "ri-archive-line",
    title: "Warehousing & Logistics",
    desc: "Honeywell RT10A and EDA10A tablets power warehouse picking, receiving, and inventory operations — with FlexRange scanning up to 35ft and all-day hot-swap batteries for 24/7 operations.",
    stat: "40% faster picking accuracy",
  },
  {
    icon: "ri-building-line",
    title: "Construction & Field Services",
    desc: "Dell Pro Rugged 13 and 14 laptops give field engineers access to BIM, CAD, and project management tools on-site — surviving 6ft drops, dust, and rain that would destroy consumer devices.",
    stat: "3x longer device lifespan",
  },
  {
    icon: "ri-truck-line",
    title: "Fleet & Transport",
    desc: "Honeywell RT10 tablets mount to forklifts and fleet vehicles via vehicle docks, providing drivers with WMS, navigation, and communication tools without leaving the cab.",
    stat: "Zero device downtime per shift",
  },
  {
    icon: "ri-government-line",
    title: "Defence & Public Safety",
    desc: "Dell Pro Rugged 10 and 12 tablets are deployed by defence and public safety agencies — FirstNet Ready with Band 14, IP66 rated, and operating from -20°F to 145°F.",
    stat: "MIL-STD-810H certified",
  },
];

const ruggedSpotlightBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://www.sedistribution.com.my/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Solutions",
      "item": "https://www.sedistribution.com.my/solutions"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Rugged Computing",
      "item": "https://www.sedistribution.com.my/solutions/rugged-computing"
    },
    {
      "@type": "ListItem",
      "position": 4,
      "name": "Rugged Devices Spotlight",
      "item": "https://www.sedistribution.com.my/rugged-spotlight"
    }
  ]
};

export default function RuggedSpotlightPage() {
  useSEO({
    title: "Rugged Tablets & Laptops Malaysia | Honeywell ScanPal, Dell Pro Rugged | SE Distribution",
    description: "Honeywell ScanPal EDA10A, RT10 rugged tablets & Dell Pro Rugged 10/12/13/14 — MIL-STD-810H, IP66, hot-swap batteries. Technology partner in Malaysia. Compare specs & request a demo.",
    canonical: "https://www.sedistribution.com.my/rugged-spotlight",
    keywords: "Honeywell rugged tablet Malaysia, Dell rugged laptop Malaysia, ScanPal EDA10A Malaysia, RT10 tablet Malaysia, Dell Pro Rugged Malaysia, MIL-SPEC tablet Malaysia, rugged device Malaysia",
    structuredData: ruggedSpotlightBreadcrumbSchema,
  });
  const [activeProduct, setActiveProduct] = useState(0);
  const [activeBrand, setActiveBrand] = useState<"all" | "Honeywell" | "Dell Rugged">("all");
  const [activeAccessoryBrand, setActiveAccessoryBrand] = useState<"Dell Rugged" | "Honeywell">("Dell Rugged");

  const filteredProducts = activeBrand === "all"
    ? products
    : products.filter((p) => p.brand === activeBrand);

  const displayProducts = filteredProducts.length > 0 ? filteredProducts : products;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">

        {/* Hero */}
        <section className="relative bg-[#1a1a1a] py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20field%20engineer%20in%20hard%20hat%20and%20safety%20vest%20working%20outdoors%20on%20job%20site%20holding%20rugged%20tablet%20device%20reviewing%20data%2C%20construction%20machinery%20in%20background%2C%20golden%20hour%20cinematic%20lighting%2C%20wide%20angle%2C%20photorealistic%2C%20high%20detail%2C%20enterprise%20field%20operations%2C%20dramatic%20sky&width=1400&height=700&seq=rug-hero-v2&orientation=landscape"
            alt="Rugged Computing Solutions"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/90 via-[#1a1a1a]/60 to-[#1a1a1a]/30"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <div className="max-w-2xl">
              <div className="flex items-center gap-4 mb-5">
                <img
                  src="https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/c119e459-59e4-4b4e-bf37-86a13fdc63a8_Honeywell-Logo.png?v=b2263903a3a5ab368f10fcd32559f8f2"
                  alt="Honeywell"
                  className="h-7 w-auto brightness-0 invert"
                />
                <span className="text-white/30">+</span>
                <span className="text-white text-sm font-bold tracking-wide">Dell Rugged Pro</span>
                <span className="text-white/40 text-sm">|</span>
                <span className="text-xs font-semibold tracking-widest uppercase text-white/50">Rugged Computing</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
                Devices Built for <strong>Where Work Actually Happens</strong>
              </h1>
              <p className="text-white/65 text-base leading-relaxed mb-8">
                Honeywell and Dell Rugged Pro deliver MIL-SPEC certified laptops, tablets, and handheld computers that survive drops, water, dust, and extreme temperatures — keeping your field teams productive in any environment.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">MIL-STD-810H Certified</span>
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">IP65/IP66 Rated</span>
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">Hot-Swap Batteries</span>
                <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">3x Longer Lifespan</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-[#1a1a1a] text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  Request a Demo
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="tel:+60364127917"
                  className="border border-white/40 text-white text-sm font-medium px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-phone-line"></i>
                  +603 6412 7917
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#1a1a1a] py-8 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "MIL-810H", label: "Ruggedness Standard" },
              { value: "IP66", label: "Dust & Water Rating" },
              { value: "3x", label: "Longer Lifespan vs Consumer" },
              { value: "2", label: "Authorized Rugged Brands" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-white mb-1">{s.value}</p>
                <p className="text-xs text-white/50 uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why Rugged */}
        <section className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Why Rugged Computing</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">Consumer Devices Don&apos;t Belong on the Job Site</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">Consumer laptops and tablets fail in field environments. Rugged devices are purpose-built to survive — and keep your teams productive where it matters most.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {whyRugged.map((w) => (
                <div key={w.title} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                    <i className={`${w.icon} text-[#1a1a1a] text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-[#1a1a1a] text-sm mb-2">{w.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Lineup */}
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Product Lineup</span>
            <h2 className="text-3xl font-bold text-[#1a1a1a]">Featured Rugged Devices</h2>
            <p className="text-gray-500 text-sm mt-3 max-w-lg mx-auto">From enterprise tablets to fully rugged laptops — we match the right device to your workflow and environment.</p>
          </div>

          {/* Brand Filter */}
          <div className="flex gap-2 justify-center mb-8 flex-wrap">
            {(["all", "Honeywell", "Dell Rugged"] as const).map((b) => (
              <button
                key={b}
                onClick={() => { setActiveBrand(b); setActiveProduct(0); }}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeBrand === b
                    ? "bg-[#1a1a1a] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {b === "all" ? "All Brands" : b}
              </button>
            ))}
          </div>

          {/* Product Tabs */}
          <div className="flex gap-2 justify-center mb-10 flex-wrap">
            {displayProducts.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setActiveProduct(i)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeProduct === i
                    ? "bg-[#1a1a1a] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {p.name}
                {p.badge === "Flagship" && (
                  <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Flagship</span>
                )}
              </button>
            ))}
          </div>

          {/* Active Product Detail */}
          {displayProducts.map((p, i) => (
            <div
              key={p.id}
              className={`flex flex-col lg:flex-row gap-12 items-start ${activeProduct === i ? "block" : "hidden"}`}
            >
              <div className="w-full lg:w-2/5 flex-shrink-0">
                <div className="w-full h-96 rounded-xl overflow-hidden bg-[#f9f7f4] flex items-center justify-center">
                  <img src={p.image} alt={p.name} className="w-full h-full object-contain" />
                </div>
              </div>
              <div className="w-full lg:w-3/5">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    p.badge === "Flagship" ? "bg-amber-100 text-amber-700" :
                    p.badge === "Popular" ? "bg-green-100 text-green-700" :
                    "bg-gray-100 text-gray-600"
                  }`}>{p.badge}</span>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{p.brand}</span>
                </div>
                <h3 className="text-2xl font-bold text-[#1a1a1a] mb-1">{p.name}</h3>
                <p className="text-gray-400 text-sm mb-6">{p.tagline}</p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {p.specs.map((s) => (
                    <div key={s.label} className="bg-[#f9f7f4] rounded-lg p-3">
                      <p className="text-xs text-gray-400 mb-0.5">{s.label}</p>
                      <p className="text-sm font-semibold text-[#1a1a1a]">{s.value}</p>
                    </div>
                  ))}
                </div>
                <div className="mb-7">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Key Features</p>
                  <ul className="space-y-2">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600">
                        <i className="ri-check-line text-[#1a1a1a] mt-0.5 flex-shrink-0"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex gap-3 flex-wrap">
                  <Link
                    to="/contact"
                    className="bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Enquire About {p.name}
                  </Link>
                  <Link
                    to="/solutions/rugged-computing"
                    className="border border-gray-200 text-gray-600 text-sm font-medium px-6 py-3 rounded-md hover:border-[#1a1a1a] hover:text-[#1a1a1a] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    View Rugged Solutions
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Rugged Accessories Section */}
        <section className="py-20 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-10">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Ecosystem</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a]">Rugged Accessories</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto leading-relaxed">
                Complete your rugged deployment with purpose-built accessories — vehicle docks, keyboard docks, battery chargers, and carrying solutions for every workflow.
              </p>
            </div>

            {/* Brand Toggle */}
            <div className="flex gap-2 justify-center mb-10">
              {(["Dell Rugged", "Honeywell"] as const).map((b) => (
                <button
                  key={b}
                  onClick={() => setActiveAccessoryBrand(b)}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeAccessoryBrand === b
                      ? "bg-[#1a1a1a] text-white"
                      : "bg-white text-gray-600 border border-gray-200 hover:border-[#1a1a1a]"
                  }`}
                >
                  {b} Accessories
                </button>
              ))}
            </div>

            {/* Dell Accessories */}
            {activeAccessoryBrand === "Dell Rugged" && (
              <div>
                <div className="flex items-center gap-4 mb-8 p-5 bg-white rounded-xl border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1a1a1a] rounded-lg flex-shrink-0">
                    <i className="ri-macbook-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1a1a1a] text-base">Dell Pro Rugged Accessories</h3>
                    <p className="text-sm text-gray-500 mt-0.5">Purpose-built accessories for Dell Pro Rugged 10, 12, 13, and 14 — designed to extend productivity in the field, vehicle, and at the desk.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {dellAccessories.map((acc) => (
                    <div key={acc.name} className="bg-white rounded-lg p-6 border border-gray-100 hover:border-[#1a1a1a]/20 transition-colors">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                        <i className={`${acc.icon} text-[#1a1a1a] text-xl`}></i>
                      </div>
                      <h4 className="font-semibold text-[#1a1a1a] text-sm mb-2">{acc.name}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed mb-4">{acc.desc}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {acc.tags.map((tag) => (
                          <span key={tag} className="text-xs bg-[#f9f7f4] text-gray-600 px-2.5 py-1 rounded-full">{tag}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 bg-white rounded-xl border border-gray-100 p-6">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Compatible With</p>
                  <div className="flex flex-wrap gap-3">
                    {["Dell Pro Rugged 10 Tablet", "Dell Pro Rugged 12 Tablet", "Dell Pro Rugged 13 Laptop", "Dell Pro Rugged 14 Laptop"].map((d) => (
                      <span key={d} className="text-xs font-medium bg-[#1a1a1a]/5 text-[#1a1a1a] px-3 py-1.5 rounded-full">{d}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Honeywell Accessories */}
            {activeAccessoryBrand === "Honeywell" && (
              <div>
                <div className="flex items-center gap-4 mb-8 p-5 bg-white rounded-xl border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#dc2626] rounded-lg flex-shrink-0">
                    <i className="ri-smartphone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1a1a1a] text-base">Honeywell Rugged Tablet Accessories</h3>
                    <p className="text-sm text-gray-500 mt-0.5">Purpose-built accessories for the Honeywell RT10A, RT10W, and ScanPal EDA10A — vehicle docks, battery chargers, carrying solutions, and workstation docks.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {honeywellAccessories.map((acc) => (
                    <div key={acc.name} className="bg-white rounded-lg p-6 border border-gray-100 hover:border-[#1a1a1a]/20 transition-colors">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#dc2626]/10 rounded-lg mb-4">
                        <i className={`${acc.icon} text-[#dc2626] text-xl`}></i>
                      </div>
                      <h4 className="font-semibold text-[#1a1a1a] text-sm mb-2">{acc.name}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed mb-4">{acc.desc}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {acc.tags.map((tag) => (
                          <span key={tag} className="text-xs bg-[#f9f7f4] text-gray-600 px-2.5 py-1 rounded-full">{tag}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 bg-white rounded-xl border border-gray-100 p-6">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Compatible With</p>
                  <div className="flex flex-wrap gap-3">
                    {["ScanPal EDA10A", "RT10 Rugged Tablet — Android", "RT10 Rugged Tablet — Windows"].map((d) => (
                      <span key={d} className="text-xs font-medium bg-[#dc2626]/5 text-[#dc2626] px-3 py-1.5 rounded-full">{d}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 bg-[#1a1a1a]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-white/40 mb-3 block">Real-World Applications</span>
              <h2 className="text-3xl font-bold text-white">Who Uses Rugged Devices in Malaysia</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {useCases.map((u) => (
                <div key={u.title} className="bg-white/5 border border-white/10 rounded-lg p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg mb-4">
                    <i className={`${u.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-white text-sm mb-2">{u.title}</h3>
                  <p className="text-white/55 text-xs leading-relaxed mb-4">{u.desc}</p>
                  <div className="border-t border-white/10 pt-3">
                    <p className="text-xs font-semibold text-white/70">{u.stat}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 bg-[#1a1a1a] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=warehouse%20logistics%20worker%20in%20safety%20vest%20scanning%20product%20barcode%20with%20rugged%20enterprise%20tablet%20in%20large%20modern%20distribution%20center%2C%20warehouse%20shelving%20rows%20in%20background%2C%20industrial%20lighting%2C%20photorealistic%2C%20high%20detail%2C%20professional%20logistics%20operations%2C%20cinematic%20depth%20of%20field&width=1400&height=500&seq=rug-cta-v2&orientation=landscape"
            alt="Deploy Rugged Devices"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/80 to-[#1a1a1a]/95"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Find the Right Rugged Device for Your Team</h2>
            <p className="text-white/60 text-sm max-w-lg mx-auto mb-8 leading-relaxed">
              Our solutions team will match the right hardware to your workflow, environment, and budget — with full deployment and after-sales support from our KL office.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-[#1a1a1a] text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Talk to a Solutions Expert
              </Link>
              <a
                href="tel:+60364127917"
                className="border border-white/40 text-white text-sm font-medium px-8 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line"></i>
                Call +603 6412 7917
              </a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
